import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { CrawlerStatusSupplier, ShopDepartment } from '@equipmyschool/emsadminweb-models';
import { ProjectTreeDialogComponent } from 'app/crawler-status/plugins/project-tree-dialog/project-tree-dialog.component';
import { CrawlerStatusService } from 'app/server/server.module';
import { UnsubscribeOnDestroy } from 'app/shared/decorators/unsubscribe-on-destroy';
import { compareByFields, getSortFn } from 'app/shared/filtered-data-source/filtered-data-source';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { EditDisplayNameDialogComponent } from './edit-display-name-dialog/edit-display-name-dialog.component';

@Component({
  selector: 'app-crawler-status-plugins',
  templateUrl: './crawler-status-plugins.component.html',
  styleUrls: ['./crawler-status-plugins.component.scss']
})
export class CrawlerStatusPluginsComponent implements OnInit, AfterViewInit {

  public emsClientUrl = environment.emsClientUrl;

  public filters = {
    Name: { Use: false, Value: '' },
    HasPlugin: { Use: true, Value: true },
    PluginEnabled: { Use: false, Value: false },
    IncludeInList: { Use: false, Value: false },
  };

  public readonly columnSortMappings = {
    OutOfDateCategories: (row: CrawlerStatusSupplier) => row.OutOfDateCategories / row.CategoryCount,
    OutOfDateProducts: (row: CrawlerStatusSupplier) => row.OutOfDateProducts / row.ProductCount,
    HomepageChecked: (row: CrawlerStatusSupplier) => parseInt(row.HomepageLastChecked, 10) || 0,
  };

  private readonly defaultCompare = [
    { fn: (row: CrawlerStatusSupplier) => !!row.HasPlugin, isAsc: false },
    { fn: (row: CrawlerStatusSupplier) => String(row.Name ?? ''), isAsc: true },
  ];

  public dataSource = new MatTableDataSource<CrawlerStatusSupplier>([]);

  public displayedColumns = ['Name', 'DisplayName', 'Logo', 'RootDepartment', 'Checkboxes', 'OutOfDateCategories', 'OutOfDateProducts', 'HomepageChecked'];

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @UnsubscribeOnDestroy()
  private subscriptions: Subscription[] = [];

  constructor(
    private server: CrawlerStatusService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.loadSuppliers();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.subscriptions.push(
      this.sort.sortChange.subscribe((val: Sort) => {
        // respect user sort; if cleared, fall back to default plugin-first ordering
        if (val.direction) {
          const fn = getSortFn(val, this.columnSortMappings);
          if (fn) {
            this.dataSource.data = this.dataSource.data.slice().sort(fn);
          }
        } else {
          this.applyDefaultSort();
        }
      })
    );
  }

  private loadSuppliers() {
    this.subscriptions.push(
      this.server.getSuppliers().pipe(map(rows => this.applySupplierFilters(rows))).subscribe(rows => {
        this.dataSource.data = rows ?? [];
        this.applyDefaultSort();
      })
    );
  }

  private applyDefaultSort() {
    this.dataSource.data = (this.dataSource.data ?? []).slice().sort((a, b) => compareByFields(a, b, this.defaultCompare));
  }

  private applySupplierFilters(rows: CrawlerStatusSupplier[]): CrawlerStatusSupplier[] {
    return (rows ?? []).filter((row) => {
      if (this.filters.Name.Use) {
        const search = (this.filters.Name.Value ?? '').toLowerCase();
        if (!String(row.Name ?? '').toLowerCase().includes(search)) {
          return false;
        }
      }
      if (this.filters.HasPlugin.Use && !!row.HasPlugin !== !!this.filters.HasPlugin.Value) {
        return false;
      }
      if (this.filters.PluginEnabled.Use && !!row.PluginEnabled !== !!this.filters.PluginEnabled.Value) {
        return false;
      }
      if (this.filters.IncludeInList.Use && !!(row as any).IncludeInList !== !!this.filters.IncludeInList.Value) {
        return false;
      }
      return true;
    });
  }

  useFilterChanged(_: string) {
    // Keep template API stable; filtering is applied on next force update.
  }

  filterValueChanged(_: string) {
    // Keep template API stable; filtering is applied on next force update.
  }

  applyFilters() {
    this.loadSuppliers();
  }

  togglePluginEnabled(supplier: CrawlerStatusSupplier) {
    this.server.togglePluginEnabled(supplier.SupplierID).subscribe(_ => {
      supplier.PluginEnabled = !supplier.PluginEnabled;
    });
  }

  toggleShowProducts(supplier: CrawlerStatusSupplier) {
    this.server.toggleShowProducts(supplier.SupplierID).subscribe(_ => {
      supplier.ShowProductsOnline = !supplier.ShowProductsOnline;
    });
  }

  toggleIncludeInList(supplier: CrawlerStatusSupplier) {
    this.server.toggleIncludeInList(supplier.SupplierID).subscribe(_ => {
      const row = supplier as any;
      row.IncludeInList = !row.IncludeInList;
    });
  }

  toggleUseCrawledPrices(supplier: CrawlerStatusSupplier) {
    this.server.toggleUseCrawledPrices(supplier.SupplierID).subscribe(_ => {
      supplier.UseCrawledPrices = !supplier.UseCrawledPrices;
    });
  }

  setRootDepartment(row: CrawlerStatusSupplier) {
    const dialogRef = this.dialog.open(ProjectTreeDialogComponent, {
      data: {
        selectedNode: row.RootDepartment
      }
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((node: ShopDepartment) => {
        if (node) {
          this.server.setRootDepartment({ SupplierID: row.SupplierID, DepartmentID: node.DepartmentID }).subscribe(() => {
            this.snackBar.open('Root department has been successfully set');
            row.RootDepartment = node;
          });
        }
      }));
  }

  formatFractionAsPercentage(fraction: number) {
    return (fraction * 100).toFixed(1) + '%';
  }

  editName(dataRow: any) {
    const dialogRef = this.dialog.open(EditDisplayNameDialogComponent, {
      data: { row: dataRow },
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((data: CrawlerStatusSupplier) => {
        if (data !== undefined) {
          console.log(data.SupplierID + ' ' + data.DisplayName);
          this.subscriptions.push(
            this.server
            .setSupplierDisplayName({ SupplierID: data.SupplierID, DisplayName: data.DisplayName })
            .subscribe(() => {
              this.snackBar.open('Supplier display name has been successfully set');
            })
          );
        }
      })
    );
  }
}


